// The sync wire protocol, version 1 (ops `repos/sync/protocol.md`). Declared here, implemented by @radish/sync.
// Requests and envelopes are strict-in; responses are lenient-out (`.loose()`), so a newer receiver may add fields.
// Every schema here is plain (no transforms): the decoded value is the wire value, so sync hashes and stores exactly
// what it received. Big integers (positions) travel as canonical decimal strings, never JSON numbers.
import { z } from 'zod';
import { IsoInstant } from '../primitives/codecs.ts';
import { LabRegisterCondition } from '../contexts/fleet/lab.ts';

export const SYNC_PROTOCOL_VERSION = 1;

export const Uuid7 = z.uuid({ version: 'v7' });

/** A canonical non-negative decimal integer, e.g. a locationPosition. */
export const PositionStr = z.string().regex(/^(0|[1-9]\d*)$/);

export const IsoDate = z.iso.date();

/** Who ran decide and recorded the fact: `dev_<uuid7>` | `node_<uuid7>` | `cloud`. */
export const ReplicaId = z.union([
  z.string().regex(/^dev_[0-9a-f-]{36}$/),
  z.string().regex(/^node_[0-9a-f-]{36}$/),
  z.literal('cloud'),
]);

/** `<wall ms, 12 hex>.<logical, 4 hex>.<replica>`: fixed width, so string order is the HLC total order. */
export const Hlc = z.string().regex(/^[0-9a-f]{12}\.[0-9a-f]{4}\.(dev_|node_|cloud)[0-9a-z_-]*$/);

/** Per-stream causal context: the highest stream-local sequence seen from each origin. */
export const VersionVector = z.record(z.string(), z.int().nonnegative());

export const SyncActor = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('staff'), employeeId: Uuid7 }),
  z.object({ kind: z.literal('user'), userId: Uuid7 }),
  z.object({ kind: z.literal('device'), deviceId: Uuid7 }),
  z.object({ kind: z.literal('agent'), agentRunId: Uuid7, onBehalfOf: Uuid7 }),
  z.object({ kind: z.literal('system'), job: z.string().max(64) }),
  z.object({ kind: z.literal('integration'), integration: z.string().max(64) }),
]);

/** The config values the origin decided against (R1: the rung snapshot wins). */
export const RungSnapshotRef = z.object({
  configCursor: z.string(),
  entityVersions: z.record(z.string(), z.int()).optional(),
  businessDate: IsoDate,
});

// ── 1. Envelopes ────────────────────────────────────────────────────────────

export const CommandEnvelope = z.strictObject({
  commandId: Uuid7,
  type: z.string().min(1).max(96),
  version: z.int().positive(),
  payload: z.unknown(),
  actor: SyncActor,
  accountId: Uuid7,
  locationId: Uuid7,
  deviceId: Uuid7.optional(),
  origin: ReplicaId,
  /** Gapless per origin; shared by the command and every event it produced (the unit of contiguity). */
  originSeq: z.int().positive(),
  issuedAt: IsoInstant,
  hlc: Hlc,
  basis: VersionVector.optional(),
  rung: RungSnapshotRef.optional(),
  correlationId: Uuid7,
  causationId: Uuid7.optional(),
  appVersion: z.string().max(32),
  contractVersion: z.int(),
  rulesVersion: z.int(),
});

export const EventEnvelope = z.strictObject({
  eventId: Uuid7,
  streamId: z.string().regex(/^[a-z_]+:[0-9a-z_-]+$/),
  streamSeq: z.int().positive(),
  type: z.string().min(1).max(96),
  schemaVersion: z.int().positive(),
  payload: z.unknown(),
  actor: SyncActor,
  accountId: Uuid7,
  locationId: Uuid7,
  deviceId: Uuid7.optional(),
  origin: ReplicaId,
  originSeq: z.int().positive(),
  occurredAt: IsoInstant,
  hlc: Hlc,
  /** Computed at the origin by core.time; receivers never recompute it. */
  businessDate: IsoDate,
  commandId: Uuid7,
  correlationId: Uuid7,
  basis: VersionVector,
  rulesVersion: z.union([z.int(), z.literal('legacy')]),
  /** Hops that forwarded it (node, cloud). */
  relayedBy: z.array(ReplicaId).max(2).optional(),
  // Assigned by receivers; absent on push.
  locationPosition: PositionStr.optional(),
  globalPosition: PositionStr.optional(),
  recordedAt: IsoInstant.optional(),
});

/** One command and the events it produced: the unit of contiguity, idempotency and transaction. */
export const PushItem = z.strictObject({
  command: CommandEnvelope,
  events: z.array(EventEnvelope).min(1).max(50),
});

// ── 2. Push ─────────────────────────────────────────────────────────────────

export const SyncErrorCode = z.enum([
  'malformed_envelope', 'unknown_event_type', 'unsupported_schema_version', 'idempotency_key_reuse',
  'payload_invalid', 'tenant_mismatch', 'origin_mismatch',
  'gap', 'revoked_device', 'unknown_tenant', 'throttled',
  'snapshot_required', 'update_required', 'protocol_unsupported', 'request_hash_mismatch',
  // A unit on a drain token (rollback freeze) pushed a sale rung after the freeze: held, never taken (cutover C3 (b)).
  // On the wire it travels as `revoked_device` with this in `cause` (@radish/sync heldStop): registers in the field read
  // only the codes above.
  'device_draining',
  // A command a rollback's review discarded (`fleet.discardDrainHolds`) pushed again: counted as seen, never applied.
  // On the wire as `revoked_device` with this in the error's details (@radish/sync fieldResult).
  'discarded_after_rollback',
  // A unit the cloud served as its node (a training location, or an install staff opened to it) pushed after its
  // location's node was claimed: held, never taken, so orders are never numbered by the cloud and the node at once. The
  // unit asks where to sync again and moves to the node (lane X, #999). On the wire as `revoked_device` with this in `cause`.
  'sync_target_moved',
  // B662 (re-review of #1680): a config fact or stamp more than a day ahead of the receiver's clock, refused and counted
  // as seen. Its own code, so it is never hidden among other payload refusals. On the wire as `payload_invalid` with this
  // in the error's details (@radish/sync fieldResult).
  'config_clock_horizon',
  // B684 (security review of #1703): a register's own config write whose employee (or approving manager) lacks the grant
  // the command needs at the location (core storeWriteRefusal), refused and counted as seen; the register drops its write.
  // On the wire as `payload_invalid` with this in the error's details (@radish/sync fieldResult).
  'config_permission_denied',
  // B684 (security re-review of #1703): a register's guarded config write the location's node refused and relayed unmarked
  // (not in `relayedBy`), for a reason the cloud cannot see itself (its own grants and checks pass): the cloud follows the
  // node's word. Only the cloud answers it, to a node. On the wire as `payload_invalid` with this in the error's details.
  'config_relay_refused',
]);

export const SyncErrorRef = z.object({ code: SyncErrorCode, message: z.string(), details: z.unknown().optional() });

export const PushRequest = z.strictObject({
  protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
  sender: z.strictObject({ replica: ReplicaId, kind: z.enum(['device', 'node']) }),
  locationId: Uuid7,
  /** Ordered by origin, then originSeq. A device sends only its own origin; a node sends many, each contiguous. */
  items: z.array(PushItem).min(1).max(200),
  /** sha256 over the items' canonical hashes, for transport integrity. */
  requestHash: z.string().length(64),
});

export const PushItemStatus = z.enum(['accepted', 'accepted_with_discrepancy', 'duplicate', 'rejected_malformed']);

export const Durability = z.object({
  origin: ReplicaId,
  /** null when answered by the cloud, which does not know what the node holds. */
  locationDurableThrough: z.int().nonnegative().nullable(),
  /** null when the node has not heard from the cloud about this origin. */
  cloudDurableThrough: z.int().nonnegative().nullable(),
  cloudDurableAsOf: IsoInstant.nullable(),
});

// ── Commander's typed device commands (lane CM 3b; ops repos/commander/plan.md §4.4) ───────────────────────────────

/**
 * The `maxSchemaVersions` key (value 1) a build declares when it reads `device_command` directives. A receiver sends
 * one to no other puller: an older build fails to decode a directive kind it does not know (lane X, 2026-10-15).
 */
export const DEVICE_COMMAND_READER = 'directive:device_command';

/**
 * The `maxSchemaVersions` key (value 1) a build declares when it shows Commander's alerts (`showAlert`, `dismissAlerts`).
 * Separate from DEVICE_COMMAND_READER: a build that runs reload, sync and info but can't show an alert would fail to
 * decode one, so a receiver sends alerts to no other puller and marks them `unsupported` for it instead (lane CM,
 * coordinator-nodded design 2026-10-16).
 */
export const DEVICE_ALERT_READER = 'directive:device_alert';

/**
 * Legacy Commander's reload, sync and seek-info, typed (legacy Ably `reload`, `sync`, `seek-info`), and its Alert and
 * Dismiss (legacy Ably `alert`, `remove-all-alerts`), which go only to DEVICE_ALERT_READER pullers.
 */
export const DeviceCommandName = z.enum(['reloadApp', 'resync', 'collectInfo', 'showAlert', 'dismissAlerts', 'labCondition', 'screenView', 'uploadLogs']);

/**
 * What Commander's own command list sends (`fleet.sendDeviceCommand`): never a lab condition, which only `lab.*` writes,
 * and never a screen view, which only `fleet.startScreenView` writes.
 */
export const CommanderCommandName = DeviceCommandName.exclude(['labCondition', 'screenView', 'uploadLogs']);

/**
 * The `maxSchemaVersions` key (value 1) a register build declares when it shares its screen with Commander (`screenView`,
 * contracts fleet/screen.ts; lane SV). A receiver sends `screenView` to no other puller and answers it `unsupported` for
 * it instead. The command's id is the view session's id.
 */
export const SCREEN_VIEW_READER = 'directive:screen_view';

/**
 * The `maxSchemaVersions` key (value 1) a register build declares when it sends its recent logs (`uploadLogs`, contracts
 * fleet/device-logs.ts; support diagnostics §4.2). A receiver sends `uploadLogs` to no other puller and answers it
 * `unsupported` for it instead.
 */
export const UPLOAD_LOGS_READER = 'directive:upload_logs';

/** The commands only an UPLOAD_LOGS_READER build receives. */
export const DEVICE_LOG_COMMANDS: readonly z.infer<typeof DeviceCommandName>[] = ['uploadLogs'];

/** The commands only a SCREEN_VIEW_READER build receives. */
export const DEVICE_SCREEN_COMMANDS: readonly z.infer<typeof DeviceCommandName>[] = ['screenView'];

/**
 * The `maxSchemaVersions` key (value 1) a register build declares when it enters staging's lab conditions on itself
 * (contracts fleet/lab.ts): only qa-staging builds do (invariant D11). A receiver sends `labCondition` to no other
 * puller and answers it `unsupported` for it instead.
 */
export const LAB_CONDITION_READER = 'directive:lab_condition';

/** The commands only a LAB_CONDITION_READER build receives. */
export const DEVICE_LAB_COMMANDS: readonly z.infer<typeof DeviceCommandName>[] = ['labCondition'];

/**
 * A `labCondition` command's order: enter this condition until `until` (at most 30 minutes after it was set), or, with
 * `condition` null, end it now. The register enforces the time limit itself too.
 */
export const DeviceLabOrder = z.strictObject({ conditionId: Uuid7, condition: LabRegisterCondition.nullable(), until: IsoInstant });

/** The commands only a DEVICE_ALERT_READER build receives. */
export const DEVICE_ALERT_COMMANDS: readonly z.infer<typeof DeviceCommandName>[] = ['showAlert', 'dismissAlerts'];

/**
 * Commander's alert, as legacy's Alert dialog sends it: a type (legacy Positive, Negative, Warning, Info) and a message
 * the register shows as plain text, never markup, until staff dismiss it. Untrusted text both ways (W-82).
 */
export const DeviceAlert = z.strictObject({
  severity: z.enum(['positive', 'negative', 'warning', 'info']),
  message: z.string().trim().min(1).max(500),
});

/**
 * What `collectInfo` may report: an allow-list of facts about the unit, never free text, a person's details or a
 * secret (lane X, 2026-10-15). Values are short plain strings.
 */
export const DeviceInfoKey = z.enum([
  'appVersion', 'buildCommit', 'contractVersion', 'osVersion', 'hardwareModel', 'storageFreeBytes', 'storageTotalBytes',
  'batteryPercent', 'charging', 'lastPullAt', 'lastPushAt', 'outboxDepth', 'connectedVia', 'nodeReachable',
  // Support diagnostics (ops plan 2026-10-17-support-diagnostics §4.1; coordinator-nodded 2026-10-17): the card
  // terminal's LAN address as `ip:port` only (so the hub can check it), its last answer, and when the app started.
  'terminalAddress', 'terminalLastAnswerAt', 'appStartedAt',
]);

/**
 * The facts every receiver reads, from before the support-diagnostics keys. `DeviceCommandResult` is strict at every hop,
 * so a key a receiver does not know refuses the whole heartbeat, beacon or poll it rides in (lane X3, 2026-10-17). A unit
 * therefore sends only the keys its receiver offered on the `collectInfo` directive (`infoKeys`), and these when none
 * were offered (an older hub or cloud).
 */
export const BASE_DEVICE_INFO_KEYS: readonly z.infer<typeof DeviceInfoKey>[] = [
  'appVersion', 'buildCommit', 'contractVersion', 'osVersion', 'hardwareModel', 'storageFreeBytes', 'storageTotalBytes',
  'batteryPercent', 'charging', 'lastPullAt', 'lastPushAt', 'outboxDepth', 'connectedVia', 'nodeReachable',
];

/**
 * The keys a unit may answer a `collectInfo` with: those its receiver offered that this build knows, or
 * BASE_DEVICE_INFO_KEYS when the directive offered none.
 */
/**
 * What a receiver offers on a `collectInfo` it passes on: the keys offered to it from upstream that it also parses (an
 * intersection, never its own full list), or none when upstream offered none. The cloud, where the command starts,
 * offers every DeviceInfoKey. So every receiver on a unit's path parses whatever the unit is offered (lane X3).
 */
export function forwardedInfoKeys(upstream: readonly string[] | null | undefined): z.infer<typeof DeviceInfoKey>[] | undefined {
  if (upstream === null || upstream === undefined) return undefined;
  const offered = new Set(upstream);
  return DeviceInfoKey.options.filter((k) => offered.has(k));
}

export function offeredInfoKeys(offered: readonly string[] | undefined): ReadonlySet<z.infer<typeof DeviceInfoKey>> {
  if (offered === undefined) return new Set(BASE_DEVICE_INFO_KEYS);
  const known = new Set<string>(DeviceInfoKey.options);
  return new Set(offered.filter((k): k is z.infer<typeof DeviceInfoKey> => known.has(k)));
}

/** A unit's answer to one command, sent on its next heartbeat. */
export const DeviceCommandResult = z.strictObject({
  commandId: Uuid7,
  state: z.enum(['acknowledged', 'completed', 'failed']),
  /** Why a command failed or was not run: it arrived expired, the unit can't run it, or running it failed. */
  failure: z.enum(['expired', 'unsupported', 'error']).optional(),
  info: z.array(z.strictObject({ key: DeviceInfoKey, value: z.string().max(64).regex(/^[\w .:+\-/%]*$/) })).max(DeviceInfoKey.options.length).optional(),
});

export const Directive = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('refresh_config') }),
  z.object({ kind: z.literal('drain_now') }),
  z.object({ kind: z.literal('reload_app') }),
  z.object({
    kind: z.literal('update_required'),
    minAppVersion: z.string(),
    /** The fact the feed is held before: an event version newer than the puller declared it reads (`maxSchemaVersions`). */
    needs: z.object({ event: z.string(), schemaVersion: z.int().positive() }).optional(),
  }),
  z.object({ kind: z.literal('snapshot_required'), reason: z.string() }),
  z.object({ kind: z.literal('revoke'), reason: z.string() }),
  z.object({ kind: z.literal('reroute'), prefer: z.enum(['node', 'cloud']), nodeAddress: z.string().optional() }),
  /**
   * A command staff sent to this unit from Commander (`fleet.sendDeviceCommand`), only to a puller declaring
   * DEVICE_COMMAND_READER. The unit acts on a `commandId` at most once, and never at or after `expiresAt`.
   */
  z.object({
    kind: z.literal('device_command'), commandId: Uuid7, command: DeviceCommandName, expiresAt: IsoInstant,
    /** `showAlert`'s type and message (only to a DEVICE_ALERT_READER puller). */
    alert: DeviceAlert.optional(),
    /** `labCondition`'s order (only to a LAB_CONDITION_READER puller, a qa-staging register). */
    lab: DeviceLabOrder.optional(),
    /**
     * `collectInfo` only: the facts every receiver on the unit's path parses (the cloud offers every DeviceInfoKey; a
     * hub passes on only what it was offered and parses, `forwardedInfoKeys`). The unit answers only the keys offered
     * that it knows (`offeredInfoKeys`); absent means BASE_DEVICE_INFO_KEYS. Plain strings, so a newer receiver's key
     * never fails an older unit's decode.
     */
    infoKeys: z.array(z.string().max(64)).max(64).optional(),
  }),
]);

export const PushStop = z.discriminatedUnion('reason', [
  z.object({ reason: z.literal('gap'), origin: ReplicaId, expectedFrom: z.int().positive() }),
  z.object({ reason: z.literal('quarantined'), cause: z.enum(['revoked_device', 'unknown_tenant', 'unknown_origin', 'request_hash_mismatch']) }),
  z.object({ reason: z.literal('throttled'), retryAfterMs: z.int() }),
  /**
   * The receiver cannot take this item yet and keeps it out of its count (a newer build's fact: an event type or version
   * it does not read, a command over several streams; or an item for another location). The push stops before it; the
   * sender keeps it and every later item, and tries again after a wait. Nothing is counted as seen, so nothing is lost.
   */
  z.object({
    reason: z.literal('update_required'),
    origin: ReplicaId,
    originSeq: z.int().positive(),
    commandId: Uuid7,
    /** One of the codes registers in the field decode (@radish/sync FIELD_HOLD_CODES); `cause` is the precise one. */
    code: SyncErrorCode,
    /** The precise reason when it is newer than what registers in the field read (`device_draining`); absent otherwise. */
    cause: SyncErrorCode.optional(),
  }),
]);

export const PushResponse = z
  .object({
    answeredBy: z.enum(['node', 'cloud']),
    results: z.array(
      z.object({
        commandId: Uuid7,
        status: PushItemStatus,
        positions: z.array(PositionStr),
        /** Deterministic uuidv5 ids (algorithms.md §6), the same on every receiver. */
        discrepancyIds: z.array(z.uuid({ version: 'v5' })).optional(),
        error: SyncErrorRef.optional(),
      }),
    ),
    stop: PushStop.optional(),
    durability: z.array(Durability),
    serverHlc: Hlc,
    directives: z.array(Directive),
  })
  .loose();

// ── 3. Pull ─────────────────────────────────────────────────────────────────

export const PullScope = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('location'), locationId: Uuid7 }),
  z.object({ kind: z.literal('device'), deviceId: Uuid7 }),
  z.object({ kind: z.literal('kitchen_station'), locationId: Uuid7, stationId: Uuid7 }),
]);

export const PullRequest = z.strictObject({
  protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
  requester: ReplicaId,
  scopes: z.array(PullScope).min(1).max(4),
  /** Opaque, minted by the answering receiver; null starts from the beginning of retention. */
  cursor: z.string().nullable(),
  limit: z.int().min(1).max(1000),
  include: z.array(z.enum(['events', 'config', 'discrepancies', 'conflicts'])),
  maxSchemaVersions: z.record(z.string(), z.int()),
  /** A node pulling from the cloud excludes what it originated or relayed itself. */
  excludeOrigins: z.array(ReplicaId).optional(),
  /**
   * Hold this pull up to this long when nothing is new for the puller, and answer the moment something is (ops plan
   * 2026-10-16-command-latency). Send it only to a receiver whose last answer said `holdsPulls`: one before it refuses
   * the field (this object is strict).
   */
  waitMs: z.int().min(0).max(25_000).optional(),
});

export const FieldStamp = z.object({
  value: z.unknown(),
  hlc: Hlc,
  origin: ReplicaId,
  /** HLC of the value the writer saw; null = written without seeing a prior value. */
  basis: Hlc.nullable(),
  commandId: Uuid7,
});

export const Tombstone = z.object({ hlc: Hlc, origin: ReplicaId, basis: Hlc.nullable(), commandId: Uuid7 });

export const ConfigDelta = z.object({
  entity: z.string(),
  id: z.string(),
  version: z.int(),
  /** Devices receive effective values; nodes receive stamps. */
  fields: z.record(z.string(), z.unknown()).optional(),
  stamps: z.record(z.string(), z.array(FieldStamp)).optional(),
  tombstone: Tombstone.optional(),
  changedAt: IsoInstant,
});

export const ConfigConflictRecord = z.object({
  /** uuidv5(entity|id|field|sorted hlcs): identical on every replica. */
  conflictId: z.uuid({ version: 'v5' }),
  entity: z.string(),
  id: z.string(),
  field: z.string(),
  candidates: z.array(FieldStamp).length(2),
  chosen: z.enum(['0', '1']),
  kind: z.enum(['field_concurrent_write', 'delete_vs_edit', 'override_vs_base']),
  status: z.enum(['open', 'kept', 'reverted']),
});

export const DiscrepancyRecord = z.object({
  /** uuidv5(kind|sorted eventIds|detail): every receiver derives the same id. */
  discrepancyId: z.uuid({ version: 'v5' }),
  kind: z.string(),
  severity: z.enum(['informational', 'attention', 'alert']),
  eventIds: z.array(Uuid7),
  detail: z.string(),
});

export const PullResponse = z
  .object({
    events: z.array(EventEnvelope),
    /** Node-grade pulls carry the command envelopes too, so the node dedupes by commandId and can re-verify. */
    commands: z.array(CommandEnvelope).optional(),
    /**
     * Node-grade pulls also carry feed markers for items the answering receiver quarantined but counted as seen (risk
     * R4), at their feed positions, so the node advances that origin past them in order instead of stalling on a gap.
     */
    quarantined: z
      .array(
        z.object({
          position: PositionStr,
          origin: ReplicaId,
          originSeq: z.int().positive(),
          commandId: z.string(),
          /** The receiver's content hash of the item, so a later push of the same item is a duplicate. */
          itemHash: z.string().length(64),
          /** In the codes every node in the field decodes: a newer code goes as its stand-in, with itself in `cause`. */
          code: SyncErrorCode,
          /** The precise code when it is newer than what nodes in the field read (`config_clock_horizon`); absent otherwise. */
          cause: SyncErrorCode.optional(),
          /** The item as received; it may not be a valid PushItem. */
          item: z.unknown(),
        }),
      )
      .optional(),
    config: z.array(ConfigDelta),
    discrepancies: z.array(DiscrepancyRecord).optional(),
    conflicts: z.array(ConfigConflictRecord).optional(),
    cursor: z.string(),
    hasMore: z.boolean(),
    /** This receiver holds pulls that ask it to (`waitMs`), up to `maxWaitMs`; absent: it answers every pull at once. */
    holdsPulls: z.object({ maxWaitMs: z.int().min(0).max(25_000) }).optional(),
    snapshotRequired: z.object({ reason: z.enum(['retention', 'schema_reset', 'forced']), snapshotUrl: z.string() }).optional(),
    serverHlc: Hlc,
    directives: z.array(Directive),
    /**
     * A location node's own reach to the cloud: how its last replication pass went and when. Its devices read the
     * store's internet from it instead of probing the cloud themselves (the card gate, NM-074; a register on a POS
     * network with no internet of its own). The cloud never sends it; an older node does not either.
     */
    upstream: z.object({ reachable: z.boolean(), checkedAt: IsoInstant }).optional(),
  })
  .loose();

// ── 5. Health report (the wire route for fleet.reportHealth) ────────────────

export const HeartbeatRequest = z.strictObject({
  protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
  role: z.enum(['device', 'node']),
  replica: ReplicaId,
  locationId: Uuid7,
  appVersion: z.string().max(32),
  contractVersion: z.int(),
  originSeqHead: z.int().nonnegative(),
  pendingCount: z.int().nonnegative(),
  oldestPendingSeconds: z.int().nonnegative(),
  lastCloudContactAt: IsoInstant.nullable(),
  clock: z.object({ wall: IsoInstant, hlc: Hlc }),
  /** The unit's answers to Commander's device commands since its last heartbeat (lane CM 3b); older builds send none. */
  commandResults: z.array(DeviceCommandResult).max(50).optional(),
  /**
   * The register is in Training mode since then (ops plans/2026-10-19-register-training-mode): its real store goes on
   * syncing and heartbeating while its screens run on a training store that never syncs, so Commander can say so.
   * Absent when it is not training, and from builds without training mode.
   */
  training: z.strictObject({ since: IsoInstant }).optional(),
  /**
   * The register is held because it cannot read a money setting (lane RG, B606 follow-up; SettingDef `failClosed`): it
   * prices nothing until the setting becomes readable. `keys` are `scope:key`, never the value. Absent when not held, and
   * from builds without it.
   */
  held: z.strictObject({ reason: z.literal('unreadable_money_setting'), keys: z.array(z.string().max(120)).min(1).max(20) }).optional(),
});

export const HeartbeatResponse = z
  .object({ serverTime: IsoInstant, serverHlc: Hlc, durability: z.array(Durability), directives: z.array(Directive) })
  .loose();

// ── 6. History (node only) ──────────────────────────────────────────────────
// A location node's copy of the legacy history the import wrote in the cloud (ops plan 2026-10-13-refund-imported-orders
// item 1): the imported orders, tenders, refunds, tills and timecards of its location inside the node's retention window,
// so a refund of an order taken in legacy is decided at the store. History has no feed entry: a device never receives
// it, and the node never pushes it back. The node pages it from its own watermark (the cloud's event position), at
// claim and after every delta, and compares a count and hash per business date with the cloud's.

/** The import contexts history is kept in (their event tables). */
export const HistoryContext = z.enum(['orders', 'payments', 'cash', 'labor']);

export const HistoryRequest = z.strictObject({
  protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
  requester: ReplicaId,
  locationId: Uuid7,
  /** The first business date the node keeps (its retention window). */
  from: IsoDate,
  /** The cloud event position the node holds history through; null starts from the beginning. */
  after: PositionStr.nullable(),
  /** A repair: only these business dates, whatever the watermark (from `after`, paged). */
  dates: z.array(IsoDate).min(1).max(62).optional(),
  limit: z.int().min(1).max(2000),
  maxSchemaVersions: z.record(z.string(), z.int()),
});

/** One imported fact as the cloud stored it; the node stores it unchanged under its own event position. */
export const HistoryFact = z.strictObject({
  context: HistoryContext,
  eventId: z.uuid(),
  streamId: z.string().min(1).max(200),
  streamSeq: z.int().positive(),
  businessDate: IsoDate,
  eventName: z.string().min(1).max(64),
  schemaVersion: z.int().positive(),
  payload: z.unknown(),
  hlc: z.string().min(1).max(64),
  occurredAt: IsoInstant,
  commandId: z.uuid(),
  /** The cloud's event position: the node's watermark after it stores the fact. */
  position: PositionStr,
});

export const HistoryResponse = z
  .object({
    facts: z.array(HistoryFact),
    /** The position to ask `after` next; null when the page was empty. */
    next: PositionStr.nullable(),
    hasMore: z.boolean(),
    /** Facts newer than the node's build reads: the page stops before the first, and the node waits for its update. */
    updateRequired: z.array(z.object({ eventName: z.string(), schemaVersion: z.int() })).optional(),
  })
  .loose();

/** One business date of history: how many imported facts, and a hash over them (event id, stream sequence, payload). */
export const HistoryDay = z.object({ businessDate: IsoDate, count: z.int().nonnegative(), hash: z.string().regex(/^[0-9a-f]{64}$/) });

export const HistoryDigestRequest = z.strictObject({
  protocolVersion: z.literal(SYNC_PROTOCOL_VERSION),
  requester: ReplicaId,
  locationId: Uuid7,
  from: IsoDate,
  /** The node's own days (the cloud records them for the migration verify). */
  days: z.array(HistoryDay).max(500),
});

export const HistoryDigestResponse = z
  .object({
    /** The business dates whose count or hash differ between the node and the cloud (either side may lack the date). */
    differing: z.array(IsoDate),
    checkedAt: IsoInstant,
  })
  .loose();

export type SyncCommandEnvelope = z.infer<typeof CommandEnvelope>;
export type SyncEventEnvelope = z.infer<typeof EventEnvelope>;
export type SyncPushItem = z.infer<typeof PushItem>;
export type SyncPushRequest = z.infer<typeof PushRequest>;
export type SyncPushResponse = z.infer<typeof PushResponse>;
export type SyncPushStatus = z.infer<typeof PushItemStatus>;
export type SyncPushStop = z.infer<typeof PushStop>;
export type SyncDurability = z.infer<typeof Durability>;
export type SyncDirective = z.infer<typeof Directive>;
export type SyncDeviceCommandResult = z.infer<typeof DeviceCommandResult>;
export type SyncErrorCodeName = z.infer<typeof SyncErrorCode>;
export type SyncPullRequest = z.infer<typeof PullRequest>;
export type SyncPullResponse = z.infer<typeof PullResponse>;
export type SyncFieldStamp = z.infer<typeof FieldStamp>;
export type SyncTombstone = z.infer<typeof Tombstone>;
export type SyncConfigConflict = z.infer<typeof ConfigConflictRecord>;
export type SyncDiscrepancy = z.infer<typeof DiscrepancyRecord>;
export type SyncHeartbeatRequest = z.infer<typeof HeartbeatRequest>;
export type SyncHeartbeatResponse = z.infer<typeof HeartbeatResponse>;
export type SyncActorValue = z.infer<typeof SyncActor>;
export type SyncHistoryRequest = z.infer<typeof HistoryRequest>;
export type SyncHistoryResponse = z.infer<typeof HistoryResponse>;
export type SyncHistoryFact = z.infer<typeof HistoryFact>;
export type SyncHistoryDay = z.infer<typeof HistoryDay>;
export type SyncHistoryDigestRequest = z.infer<typeof HistoryDigestRequest>;
export type SyncHistoryDigestResponse = z.infer<typeof HistoryDigestResponse>;
